import { styled, Theme, TextField } from '@nexelem/deprecated-theme';
import { InternalFieldProps } from './InternalField';

type SizeProp = Required<InternalFieldProps>['size'];

export type StyledProps = Pick<InternalFieldProps, 'error' | 'showErrorBorder'> & {
  readonly withStartIcon?: boolean;
  readonly withEndIcon?: boolean;
  readonly readOnly?: boolean;
  readonly disabled?: boolean;
  readonly textAlign?: 'left' | 'right' | 'center';
  readonly isJumbo?: boolean;
  size?: SizeProp;
};

export const StyledTextField = styled(TextField, {
  shouldForwardProp: (propName: string) =>
    !['withStartIcon', 'withEndIcon', 'error', 'showErrorBorder', 'maxLength', 'textAlign'].includes(propName),
})<StyledProps>(({
  theme,
  withStartIcon,
  withEndIcon,
  disabled,
  error,
  readOnly,
  showErrorBorder,
  size = 'medium',
  textAlign,
}) => {
  const {
    spacing,
    typography,
    palette: { getColor },
    client: {
      brandColors: { statuses },
      inputs: { defaultColors },
    },
  } = theme as Theme;

  const isJumboSize = size === 'jumbo';

  const labelStyles = {
    ...(isJumboSize ? typography.titilliumWebSemiBold20 : typography.titilliumWebSemiBold14),
    paddingLeft: spacing(2),
    paddingBottom: spacing(2),
    overflow: 'unset',
    color: getColor(error ? defaultColors.error.inputColor : defaultColors.defaultState.labelColor),
    position: 'relative',
    transform: 'none',
    marginLeft: 0,
    pointerEvents: 'auto',
  };

  const getInputStyles = () => {
    const inputPadding: Record<SizeProp, [number, number, number, number]> = {
      small: [1, withEndIcon ? 2 : 0.5, 1, 0],
      medium: [2, withEndIcon ? 2 : 0.5, 2, 0],
      large: [3, withEndIcon ? 2 : 0.5, 3, 0],
      xl: [6, withEndIcon ? 2 : 0.5, 6, 0],
      jumbo: [8.5, withEndIcon ? 2 : 0.5, 8.25, 0],
    };

    return {
      ...(isJumboSize ? typography.sourceSansPro24Regular : typography.sourceSansPro18Regular),
      padding: spacing(...inputPadding[size as SizeProp]),
      height: 'initial',
      textAlign: textAlign,
      color: getColor(error ? defaultColors.error.inputColor : defaultColors.defaultState.inputColor),
      textFillColor: getColor(error ? defaultColors.error.inputColor : defaultColors.defaultState.inputColor),
      '&::placeholder': {
        fontStyle: 'italic',
        color: getColor(defaultColors.defaultState.placeholderColor),
        opacity: '1 !important', // !important exist in base component :(
        textFillColor: getColor(defaultColors.defaultState.placeholderColor),
        overflow: 'visible',
      },
    };
  };

  const getFieldStartIconStyles = () => {
    return {
      paddingRight: spacing(2),
      color: getColor(error ? defaultColors.error.startIconColor : defaultColors.defaultState.startIconColor),
      fontSize: isJumboSize ? '1.5rem' : '1rem',
    };
  };

  const getMuiOutlinedInputRootStyles = () => {
    const horizontalPadding: Record<Required<StyledProps>['size'], number> = {
      small: 2,
      medium: 2,
      large: 4,
      xl: 6,
      jumbo: 4,
    };

    return {
      transition: 'all 150ms ease',
      outline: `${spacing(0.25)} transparent solid`,
      padding: spacing(0, horizontalPadding[size as SizeProp]),
      '&.MuiInputBase-sizeSmall': {
        paddingTop: 0,
        paddingBottom: 0,
      },
      '&.Mui-focused': {
        borderColor: getColor(error ? defaultColors.error.borderColor : defaultColors.active),
        borderWidth: spacing(0.25),
        outlineWidth: spacing(0.25),
        outlineColor: getColor(error ? defaultColors.error.borderColor : defaultColors.active),
      },
    };
  };

  function getCustomRangeFieldStyles() {
    return {
      fontStyle: 'italic',
      '& input': {
        textAlign: 'right',
        letterSpacing: spacing(0.25),
      },
    };
  }

  const baseStyled = {
    '&& label': labelStyles,
    '&&&& input, &&&& textarea': getInputStyles(),
    '.field-start-icon': getFieldStartIconStyles(),
    '&& .MuiOutlinedInput-root': getMuiOutlinedInputRootStyles(),
    '&.customRangeField': getCustomRangeFieldStyles(),
  };

  if (readOnly) {
    return {
      ...baseStyled,
      color: getColor(defaultColors.disabled.color),
      '.field-start-icon': {
        ...baseStyled['.field-start-icon'],
        color: getColor(defaultColors.disabled.startIconColor),
      },
      fieldset: {
        zIndex: -1,
        border: 'none',
      },
    };
  }

  if (disabled) {
    return {
      ...baseStyled,
      color: getColor(defaultColors.disabled.color),
      '.field-start-icon': {
        ...baseStyled['.field-start-icon'],
        color: getColor(defaultColors.disabled.startIconColor),
      },
      fieldset: {
        zIndex: -1,
        border: 'none',
        backgroundColor: getColor(defaultColors.disabled.backgroundColor),
      },
      '.MuiInputBase-root': {
        color: getColor(defaultColors.disabled.color),
        backgroundColor: getColor(defaultColors.disabled.backgroundColor),
      },
    };
  }
  return {
    ...baseStyled,
    '.MuiFormHelperText-root': labelStyles,
    '.MuiInputBase-root': {
      transition: 'all 150ms ease',
      paddingLeft: withStartIcon ? spacing(2) : undefined,
      border: `${spacing(0.25)} solid`,
      borderColor: getColor(showErrorBorder ? statuses.error : defaultColors.defaultState.borderColor),
      backgroundColor: getColor(defaultColors.defaultState.backgroundColor),
      outline: `${spacing(0.25)} transparent solid`,
      fieldset: {
        zIndex: -1,
        backgroundColor: getColor(defaultColors.defaultState.backgroundColor),
        border: `${spacing(0.25)} solid ${getColor(defaultColors.defaultState.borderColor)}`,
      },
      '&:hover': {
        borderColor: getColor(error ? defaultColors.error.borderColor : defaultColors.hover),
        borderWidth: spacing(0.25),
      },
      '&:focus': {
        borderColor: getColor(error ? defaultColors.error.borderColor : defaultColors.focus),
        borderWidth: spacing(0.25),
        outlineWidth: spacing(0.25),
        outlineColor: getColor(error ? defaultColors.error.borderColor : defaultColors.focus),
      },
    },
    '.MuiInputBase-root:hover fieldset': {
      borderColor: getColor(defaultColors.hover),
    },
    '.MuiInputBase-root:active fieldset': {
      borderColor: getColor(defaultColors.active),
      borderWidth: spacing(0.5),
    },
    '.MuiInputBase-root.Mui-focused fieldset': {
      borderColor: getColor(defaultColors.focus),
      borderWidth: spacing(1),
    },
    '.MuiFormLabel-asterisk.MuiInputLabel-asterisk': {
      color: getColor(defaultColors.error.inputColor),
    },
  };
});
